import React, { useContext, useEffect, useState } from "react";
import Header from "../components/Header/Header";
import Footer from "../components/shared/Footer";
import { Helmet } from "react-helmet-async";
import { scrollToTop, showToastMessage, snakeToCamel } from "../common/Helper";
import {} from "../services/ProductServices";
import { useLocation, useParams } from "react-router-dom";
import { StyleContext } from "../context/StyleContextProvider";
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Divider,
  Grid,
  Rating,
  TextField,
  Typography,
  useMediaQuery,
  Alert,
  MenuItem,
} from "@mui/material";
import { Wallpaper } from "../components/shared/Wallpaper";
import { ProductImages } from "../components/Product/ProductImages";
import { StarOutline } from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { getProfile } from "../reduxenv/Slices/ProfileSlice";
import { AppColors } from "../common/AppColors";
import useErrorPage from "../hooks/useErrorPage";
import {
  useGetServerProductQuery,
  useSubmitQuoteMutation,
} from "../services/ServerProductService";
import { ServerProductModel } from "../models/ServerProductModel";
import { setIsLoading } from "../reduxenv/Slices/AppSlice";
import { useFormatCurrency } from "../hooks/useFormatCurrency";

const ServerProductQuotationForm: React.FC = () => {
  const isMobile = useMediaQuery("(max-width: 786px)");
  const styleContext = useContext(StyleContext);
  const [styles, setStyles] = useState(
    styleContext.getComponentStyle("productPage"),
  );
  const dispatch = useDispatch();
  const location = useLocation();
  const { id } = useParams();
  const productId = Number(id);
  const { data: productResponse, isFetching: isFetchingProduct } =
    useGetServerProductQuery(
      { productId: productId! },
      { skip: !!location.state },
    );

  const [product, setProduct] = useState<ServerProductModel>(location.state);
  const profile = useSelector(getProfile);

  const [processor, setProcessor] = useState<number | "">("");
  const [memory, setMemory] = useState<number | "">("");
  const [memoryQty, setMemoryQty] = useState<number>(2);

  const [storage, setStorage] = useState<number | "">("");
  const [storageQty, setStorageQty] = useState<number>(1);
  const [opticalDrive, setOpticalDrive] = useState<number | "">("");
  const [opticalDriveQty, setOpticalDriveQty] = useState<number>(1);

  const [raidController, setRaidController] = useState<number | "">("");
  const [nic, setNic] = useState<number | "">("");
  const [warranty, setWarranty] = useState<number | "">("");

  // Quotation Form States
  const [quantity, setQuantity] = useState<number>(1);
  const [comments, setComments] = useState<string>("");
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [toErrorPage] = useErrorPage();
  const [submitQuote, { isLoading: isSubmitting }] = useSubmitQuoteMutation();
  const { formatCurrency } = useFormatCurrency();

  useEffect(() => {
    setStyles(styleContext.getComponentStyle("productPage"));
    scrollToTop();
  }, [isMobile]);

  useEffect(() => {
    if (productResponse?.response) {
      let tempProduct = snakeToCamel(
        productResponse.response,
      ) as ServerProductModel;
      if (tempProduct) {
        setProduct(tempProduct);
      } else {
        toErrorPage({ message: "Product Not Found!" });
      }
    }
  }, [productResponse]);

  useEffect(() => {
    dispatch(setIsLoading(isFetchingProduct));
  }, [isFetchingProduct]);

  const getOptions = (category: string) =>
    product?.options?.filter(
      (o) => o.category.toUpperCase() === category.toUpperCase(),
    ) || [];

  const getOptionPrice = (id: number | "") => {
    if (!id) return 0;

    return Number(product?.options?.find((o) => o.id === id)?.price || 0);
  };
  const getOptionName = (id: number | "") => {
    if (!id) return "";

    return product?.options?.find((o) => o.id === id)?.name || "";
  };

  const getOptionQtyRange = (id: number | "", fallbackMax = 16) => {
    if (!id) return { min: 1, max: fallbackMax };

    const option = product?.options?.find((o) => o.id === id) as any;

    const min = Number(
      option?.minQty ?? option?.minQuantity ?? option?.min_quantity ?? 1,
    );
    const max = Number(
      option?.maxQty ??
        option?.maxQuantity ??
        option?.max_quantity ??
        fallbackMax,
    );

    return {
      min: Number.isFinite(min) && min > 0 ? min : 1,
      max: Number.isFinite(max) && max >= min ? max : fallbackMax,
    };
  };

  const opticalDriveQtyRange = getOptionQtyRange(opticalDrive, 16);
  const storageQtyRange = getOptionQtyRange(storage, 16);
  const memoryQtyRange = getOptionQtyRange(memory, 4);

  const estimatedPrice = Number(
    (product?.price || 0) +
      getOptionPrice(processor) +
      getOptionPrice(memory) * memoryQty +
      getOptionPrice(storage) * storageQty +
      getOptionPrice(opticalDrive) * opticalDriveQty +
      getOptionPrice(raidController) +
      getOptionPrice(nic) +
      getOptionPrice(warranty),
  );

  const totalPrice = formatCurrency(estimatedPrice * quantity);

  const handleSubmitQuotation = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!product) return;
    let message = "";
    if (!quantity || quantity < 1) message = "Invalid quantity";
    if (!processor) message = "Select processor";
    if (!memory) message = "Select memory";
    if (!storage) message = "Select storage";
    if (!warranty) message = "Select warranty";
    if (message != "") return showToastMessage("error", message);

    try {
      const selectedOptions = [
        processor && product.options?.find((o) => o.id === processor),
        memory && product.options?.find((o) => o.id === memory),
        storage && product.options?.find((o) => o.id === storage),
        opticalDrive && product.options?.find((o) => o.id === opticalDrive),
        raidController && product.options?.find((o) => o.id === raidController),
        nic && product.options?.find((o) => o.id === nic),
        warranty && product.options?.find((o) => o.id === warranty),
      ].filter(Boolean) as any[];

      const res = await submitQuote({
        productId: product.id,
        customerName: profile?.firstName + " " + profile?.lastName || "",
        email: profile?.email || "",
        //company: profile?.compa || "",
        phone: profile?.phone || "",
        //message: comments,
        quantity,
        selectedOptions,
      }).unwrap();

      if (res.status === "success") {
        setSubmitSuccess(true);

        // reset
        setQuantity(1);
        setProcessor("");
        setMemory("");
        setStorage("");
        setOpticalDrive("");
        setOpticalDriveQty(1);
        setRaidController("");
        setNic("");
        setWarranty("");
        setComments("");

        setTimeout(() => setSubmitSuccess(false), 3000);
      }
    } catch (err) {
      console.error(err);
      alert("Failed to submit quotation");
    }
  };

  return (
    <>
      <Helmet>
        <title>{product?.name || "Quotation Request"}</title>
      </Helmet>
      <Header />
      <Grid container justifyContent={"center"}>
        <Wallpaper
          content={
            <Box sx={styles.wallpaperContent}>
              <Grid container alignItems={"center"} justifyContent={"unset"}>
                <Grid item xs={12} md={12}>
                  <Typography variant="h1" sx={styles.productName}>
                    {product?.name || "Product Quotation"}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          }
        />
        <Container maxWidth={"lg"} sx={{ py: 4 }}>
          {submitSuccess && (
            <Alert severity="success" sx={{ mb: 3 }}>
              Quotation request submitted successfully! We'll contact you soon.
            </Alert>
          )}

          <Grid container spacing={4}>
            {/* Product Details Section */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h5" gutterBottom>
                    Product Details
                  </Typography>
                  <Divider sx={{ mb: 2 }} />

                  {/* Product Image */}
                  {product?.images && product.images.length > 0 && (
                    <Box sx={{ mb: 3 }}>
                      <ProductImages
                        imageUrls={product.images.map((img) => img.url)}
                      />
                    </Box>
                  )}

                  {/* SKU and Rating */}
                  <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={6}>
                      <Typography variant="subtitle2" color="textSecondary">
                        SKU
                      </Typography>
                      <Typography variant="body1">
                        {product?.sku || "N/A"}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="subtitle2" color="textSecondary">
                        Rating
                      </Typography>
                      <Rating
                        value={
                          product?.averageRating
                            ? Number(product.averageRating)
                            : 0
                        }
                        emptyIcon={
                          <StarOutline sx={styles.noRating} fontSize="small" />
                        }
                        readOnly
                        size="small"
                      />
                    </Grid>
                  </Grid>

                  {/* Price */}
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Unit Price
                    </Typography>

                    <Typography
                      variant="h6"
                      sx={{ color: AppColors.MainColor }}
                    >
                      {Number(product?.price)?.toFixed(2)}{" "}
                      {profile?.country?.currency || ""}
                    </Typography>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        color: "error.main",
                        fontWeight: 600,
                        mt: 0.5,
                      }}
                    >
                      ⚠ Important: This price is an estimate and may change
                      based on final supplier quotation.
                    </Typography>
                  </Box>

                  {/* Description */}
                  <Typography
                    variant="subtitle2"
                    color="textSecondary"
                    sx={{ mb: 1 }}
                  >
                    Description
                  </Typography>
                  {product?.shortDescription && (
                    <div
                      style={{ marginBottom: "16px", lineHeight: "1.6" }}
                      dangerouslySetInnerHTML={{
                        __html: product.shortDescription,
                      }}
                    />
                  )}
                  {product?.description && (
                    <div
                      style={{ lineHeight: "1.6" }}
                      dangerouslySetInnerHTML={{
                        __html: product.description,
                      }}
                    />
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Quotation Form Section */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h5" gutterBottom>
                    Request Quotation
                  </Typography>
                  <Divider sx={{ mb: 3 }} />
                  <Typography
                    variant="body2"
                    sx={{ color: "text.secondary", fontWeight: 500, mb: 1 }}
                  >
                    Fields marked with an asterisk (*) are mandatory and must be
                    completed in order to submit your quotation request.
                  </Typography>
                  <Box
                    component="form"
                    onSubmit={handleSubmitQuotation}
                    noValidate
                  >
                    {/* Product Info - Read Only */}
                    <TextField
                      fullWidth
                      label="Product Name"
                      value={product?.name || ""}
                      disabled
                      margin="normal"
                      variant="outlined"
                    />

                    {/* Quantity */}
                    <TextField
                      fullWidth
                      label="Quantity *"
                      type="number"
                      value={quantity}
                      onChange={(e) => {
                        const val = parseInt(e.target.value);
                        if (val > 0) setQuantity(val);
                      }}
                      margin="normal"
                      variant="outlined"
                      inputProps={{ min: "1", step: "1" }}
                      required
                      helperText="Enter the quantity you need"
                    />

                    {/* Processor Dropdown */}
                    <TextField
                      select
                      fullWidth
                      label="Processor *"
                      value={processor}
                      onChange={(e) => setProcessor(Number(e.target.value))}
                      margin="normal"
                      variant="outlined"
                      required
                    >
                      <MenuItem value="">-- Select Processor --</MenuItem>
                      {getOptions("CPU").map((item) => (
                        <MenuItem key={item.id} value={item.id}>
                          {item.name}
                        </MenuItem>
                      ))}
                    </TextField>

                    {/* Memory Dropdown */}
                    <TextField
                      select
                      fullWidth
                      label="Memory *"
                      value={memory}
                      onChange={(e) => setMemory(Number(e.target.value))}
                      margin="normal"
                      variant="outlined"
                      required
                    >
                      <MenuItem value="">-- Select Memory --</MenuItem>
                      {getOptions("MEMORY").map((item) => (
                        <MenuItem key={item.id} value={item.id}>
                          {item.name}
                        </MenuItem>
                      ))}
                    </TextField>

                    {/* Memory Quantity */}
                    <TextField
                      fullWidth
                      label="Memory Quantity"
                      type="number"
                      value={memoryQty}
                      onChange={(e) => setMemoryQty(Number(e.target.value))}
                      margin="normal"
                      variant="outlined"
                      inputProps={{
                        min: memoryQtyRange.min,
                        max: memoryQtyRange.max,
                      }}
                    />

                    {/* Storage Dropdown */}
                    <TextField
                      select
                      fullWidth
                      label="Storage *"
                      value={storage}
                      onChange={(e) => setStorage(Number(e.target.value))}
                      margin="normal"
                      variant="outlined"
                      required
                    >
                      <MenuItem value="">-- Select Storage --</MenuItem>
                      {getOptions("STORAGE").map((item) => (
                        <MenuItem key={item.id} value={item.id}>
                          {item.name}
                        </MenuItem>
                      ))}
                    </TextField>

                    {/* Storage Quantity */}
                    <TextField
                      fullWidth
                      label="Storage Quantity"
                      type="number"
                      value={storageQty}
                      onChange={(e) => setStorageQty(Number(e.target.value))}
                      margin="normal"
                      variant="outlined"
                      inputProps={{
                        min: storageQtyRange.min,
                        max: storageQtyRange.max,
                      }}
                    />

                    {/* Optical Drive Dropdown */}
                    <TextField
                      select
                      fullWidth
                      label="Optical Drive"
                      value={opticalDrive}
                      onChange={(e) => setOpticalDrive(Number(e.target.value))}
                      margin="normal"
                      variant="outlined"
                      required
                    >
                      <MenuItem value="">-- Select Optical Drive --</MenuItem>
                      {getOptions("OPTICAL").map((item) => (
                        <MenuItem key={item.id} value={item.id}>
                          {item.name}
                        </MenuItem>
                      ))}
                    </TextField>

                    {/* Optical Drive Quantity */}
                    <TextField
                      fullWidth
                      label="Optical Drive Quantity"
                      type="number"
                      value={opticalDriveQty}
                      onChange={(e) =>
                        setOpticalDriveQty(Number(e.target.value))
                      }
                      margin="normal"
                      variant="outlined"
                      inputProps={{
                        min: opticalDriveQtyRange.min,
                        max: opticalDriveQtyRange.max,
                      }}
                    />

                    {/* RAID Controller */}
                    <TextField
                      select
                      fullWidth
                      label="RAID Controller"
                      value={raidController}
                      onChange={(e) =>
                        setRaidController(Number(e.target.value))
                      }
                      margin="normal"
                      variant="outlined"
                    >
                      <MenuItem value="">-- Select RAID Controller --</MenuItem>
                      {getOptions("RAID").map((item) => (
                        <MenuItem key={item.id} value={item.id}>
                          {item.name}
                        </MenuItem>
                      ))}
                    </TextField>

                    {/* Network Card */}
                    <TextField
                      select
                      fullWidth
                      label="Network Card"
                      value={nic}
                      onChange={(e) => setNic(Number(e.target.value))}
                      margin="normal"
                      variant="outlined"
                    >
                      <MenuItem value="">-- Select Network Card --</MenuItem>
                      {getOptions("NIC").map((item) => (
                        <MenuItem key={item.id} value={item.id}>
                          {item.name}
                        </MenuItem>
                      ))}
                    </TextField>

                    {/* Warranty */}
                    <TextField
                      select
                      fullWidth
                      label="Warranty *"
                      value={warranty}
                      onChange={(e) => setWarranty(Number(e.target.value))}
                      margin="normal"
                      variant="outlined"
                      required
                    >
                      <MenuItem value="">-- Select Warranty --</MenuItem>
                      {getOptions("WARRANTY").map((item) => (
                        <MenuItem key={item.id} value={item.id}>
                          {item.name}
                        </MenuItem>
                      ))}
                    </TextField>

                    {/* Comments */}
                    <TextField
                      fullWidth
                      label="Comments"
                      value={comments}
                      onChange={(e) => setComments(e.target.value)}
                      margin="normal"
                      variant="outlined"
                      multiline
                      rows={3}
                      placeholder="Add any additional information or special requirements..."
                      helperText="Optional: Add any special requirements or comments"
                    />

                    {/* Configuration Summary */}
                    <Box
                      sx={{
                        mt: 3,
                        p: 2,
                        backgroundColor: "#f5f5f5",
                        borderRadius: 1,
                        border: "1px solid #e0e0e0",
                      }}
                    >
                      <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                        Configuration Summary
                      </Typography>

                      <Grid container spacing={1}>
                        <Grid item xs={8}>
                          <Typography variant="body2">Base Server:</Typography>
                        </Grid>
                        <Grid item xs={4} sx={{ textAlign: "right" }}>
                          <Typography variant="body2" fontWeight="bold">
                            {(product?.price || 0).toFixed(2)}
                          </Typography>
                        </Grid>

                        <Grid item xs={8}>
                          <Typography variant="body2">CPU:</Typography>
                        </Grid>
                        <Grid item xs={4} sx={{ textAlign: "right" }}>
                          <Typography variant="body2" fontWeight="bold">
                            {getOptionName(processor) || "--"}
                          </Typography>
                        </Grid>

                        <Grid item xs={8}>
                          <Typography variant="body2">
                            Memory: {memoryQty} ×
                          </Typography>
                        </Grid>
                        <Grid item xs={4} sx={{ textAlign: "right" }}>
                          <Typography variant="body2" fontWeight="bold">
                            {getOptionName(memory) || "--"}
                          </Typography>
                        </Grid>

                        <Grid item xs={8}>
                          <Typography variant="body2">
                            Storage: {storageQty} ×
                          </Typography>
                        </Grid>
                        <Grid item xs={4} sx={{ textAlign: "right" }}>
                          <Typography variant="body2" fontWeight="bold">
                            {getOptionName(storage) || "--"}
                          </Typography>
                        </Grid>

                        <Grid item xs={8}>
                          <Typography variant="body2">RAID:</Typography>
                        </Grid>
                        <Grid item xs={4} sx={{ textAlign: "right" }}>
                          <Typography variant="body2" fontWeight="bold">
                            {getOptionName(raidController) || "--"}
                          </Typography>
                        </Grid>

                        <Grid item xs={8}>
                          <Typography variant="body2">NIC:</Typography>
                        </Grid>
                        <Grid item xs={4} sx={{ textAlign: "right" }}>
                          <Typography variant="body2" fontWeight="bold">
                            {getOptionName(nic) || "--"}
                          </Typography>
                        </Grid>

                        <Grid item xs={8}>
                          <Typography variant="body2">Warranty:</Typography>
                        </Grid>
                        <Grid item xs={4} sx={{ textAlign: "right" }}>
                          <Typography variant="body2" fontWeight="bold">
                            {getOptionName(warranty) || "--"}
                          </Typography>
                        </Grid>
                      </Grid>

                      <Divider sx={{ my: 2 }} />

                      <Grid container spacing={1}>
                        <Grid item xs={8}>
                          <Typography variant="h6">
                            Total Price (Qty: {quantity}):
                          </Typography>
                        </Grid>
                        <Grid item xs={4} sx={{ textAlign: "right" }}>
                          <Typography
                            variant="h6"
                            sx={{
                              color: AppColors.MainColor,
                              fontWeight: "bold",
                            }}
                          >
                            {totalPrice}
                          </Typography>
                        </Grid>
                      </Grid>

                      <Typography
                        variant="caption"
                        color="textSecondary"
                        sx={{ mt: 1, display: "block" }}
                      >
                        {profile?.country?.isTaxIncluded
                          ? "Including GST "
                          : "Excluding GST "}
                        {profile?.country?.taxPercentage || "0"}%
                      </Typography>
                    </Box>

                    {/* Submit Button */}
                    <Button
                      fullWidth
                      variant="contained"
                      color="primary"
                      size="large"
                      type="submit"
                      disabled={isSubmitting}
                      sx={{ mt: 3 }}
                    >
                      {isSubmitting
                        ? "Submitting..."
                        : "Submit Quotation Request"}
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Grid>
      <Footer />
    </>
  );
};

export default ServerProductQuotationForm;
